import apiClient from './apiClient';
import Papa from 'papaparse';
import { XMLParser, XMLValidator } from 'fast-xml-parser';

/**
 * Validation Rules Service
 * Handles fetching, caching, and managing validation rules from the API
 * 
 * IMPORTANT: Uses lazy loading - validation rules are loaded on-demand when first needed,
 * not during application startup or login. This improves performance and reduces
 * unnecessary API calls for users who don't upload files.
 */
class ValidationRulesService {
  constructor() {
    this.rules = null;
    this.isLoaded = false;
    this.loadPromise = null;
    this.vueContext = null;
  }

  /**
   * Bind Vue context to access $apiClient and $showNotification
   * @param {Object} vueContext - Vue component instance
   */
  bindVueContext(vueContext) {
    this.vueContext = vueContext;
  }

  /**
   * Fetch validation rules from API and cache them with retry mechanism
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Promise<Object>} The validation rules object
   */
  async loadRules(maxRetries = 3) {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this._fetchRulesWithRetry(maxRetries);
    return this.loadPromise;
  }

  /**
   * Fetch validation rules with retry logic
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise<Object>} The validation rules object
   */
  async _fetchRulesWithRetry(maxRetries) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Attempting to load validation rules (attempt ${attempt}/${maxRetries})`);
        const rules = await this._fetchRules();
        return rules;
      } catch (error) {
        lastError = error;
        console.warn(`❌ Validation rules loading attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
          console.log(`⏳ Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed
    throw new Error(`Failed to load validation rules after ${maxRetries} attempts. Last error: ${lastError.message}`);
  }

  /**
   * Load rules with specific retry logic for post-CSV-formatting scenarios
   * @param {string} ruleType - The rule type to load
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise<Object|null>} The rules or null if not found
   */
  async _loadRulesWithRetryAfterFormatting(ruleType, maxRetries = 2) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Loading rules after CSV formatting (attempt ${attempt}/${maxRetries})`);
        
        // Force a fresh load if this is a retry attempt
        if (attempt > 1) {
          console.log('🔄 Retry detected - clearing cache and forcing fresh rule load');
          this.clearCache();
        }
        
        const rules = await this.getRulesByType(ruleType);
        
        if (rules) {
          console.log(`✅ Rules loaded successfully on attempt ${attempt}`);
          return rules;
        } else {
          console.warn(`⚠️ No rules found for type ${ruleType} on attempt ${attempt}`);
          return null; // Not an error - just no rules found
        }
        
      } catch (error) {
        lastError = error;
        console.warn(`❌ Rules loading attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          // Shorter delay for post-formatting retries since CSV was already processed
          const delay = 500 * attempt; // 500ms, 1s
          console.log(`⏳ Retrying rules load in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed - throw the last error
    throw new Error(`Failed to load validation rules after CSV formatting (${maxRetries} attempts). Last error: ${lastError.message}. CSV was formatted successfully but rules loading consistently failed.`);
  }

  async _fetchRules() {
    try {
      // Use bound Vue context or fallback to direct import
      const client = this.vueContext?.$apiClient || apiClient;
      const response = await client.getValidationRules();
      
      if (response.data) {
        // Validate rule structure before using
        if (!this._validateRuleStructure(response.data.resultData)) {
          throw new Error('Invalid validation rules structure received from API');
        }
        this.rules = response.data.resultData;
        this.isLoaded = true;
        console.log('✅ Validation rules loaded successfully from API');
        return this.rules;
      } else {
        throw new Error('No validation rules received from API');
      }
    } catch (error) {
      console.error('❌ CRITICAL: Failed to fetch validation rules from API:', error);
      // NO FALLBACK - system must use API rules only
      throw new Error(`Validation rules must come from API. System cannot proceed without proper validation rules. Original error: ${error.message}`);
    }
  }

  /**
   * Validate the structure of validation rules received from API
   * @param {Object} rules - Rules object from API
   * @returns {boolean} True if structure is valid
   */
  _validateRuleStructure(rules) {
    if (!rules || typeof rules !== 'object') {
      console.error('Validation rules: not an object');
      return false;
    }
    
    if (!rules.validationRules || typeof rules.validationRules !== 'object') {
      console.error('Validation rules: missing validationRules section');
      return false;
    }
    
    if (!rules.globalSettings || typeof rules.globalSettings !== 'object') {
      console.error('Validation rules: missing globalSettings section');
      return false;
    }
        
    console.log('✅ Validation rules structure verified');
    return true;
  }

  /**
   * Get validation rules (load from API if not already loaded)
   * @returns {Promise<Object>} The validation rules object
   */
  async getRules() {
    if (!this.isLoaded) {
      await this.loadRules();
    }
    return this.rules;
  }

  /**
   * Get rules for a specific validation type
   * @param {string} ruleType - The rule type (e.g., 'customCountries', 'xmlValidation')
   * @returns {Promise<Object|null>} The specific rule set or null if not found
   */
  async getRulesByType(ruleType) {
    const rules = await this.getRules();
    return rules?.validationRules?.[ruleType] || null;
  }

  /**
   * Get global settings
   * @returns {Promise<Object>} Global validation settings
   */
  async getGlobalSettings() {
    const rules = await this.getRules();
    return rules?.globalSettings || this._getDefaultGlobalSettings();
  }

  /**
   * Check if file is a CSV file
   * @param {File|Object} file - File to check
   * @returns {boolean} True if file is CSV
   */
  _isCsvFile(file) {
    if (!file) return false;
    
    const fileName = file.name || '';
    const fileType = file.type || '';
    
    // Check by file extension
    const isCsvExtension = fileName.toLowerCase().endsWith('.csv');
    
    // Check by MIME type
    const isCsvMimeType = fileType === 'text/csv' || fileType === 'application/csv';
    
    return isCsvExtension || isCsvMimeType;
  }

  /**
   * Format CSV file through API before validation
   * @param {File} file - CSV file to format
   * @param {string} ruleType - Rule type for CSV formatting (e.g., 'customCountries', 'pkycFile')
   * @returns {Promise<{file: File, content: string}>} Formatted file and content
   */
  async _formatCsvFileBeforeValidation(file, ruleType = null) {
    if (!this._isCsvFile(file)) {
      console.log('⚠️ File is not CSV, skipping formatting');
      const content = await file.text();
      return { file, content };
    }

    // IMPORTANT: Skip CSV formatting for line-by-line files (like excludedWords)
    // These files should NOT be parsed as CSV - each line is a complete value
    if (ruleType === 'excludedWords') {
      console.log('⚠️ excludedWords detected - skipping CSV formatting (line-by-line mode)');
      const content = typeof file.text === 'function' ? await file.text() : String(file);
      return { file, content };
    }

    // CRITICAL: Verify file object is valid before attempting API call
    if (!file || !(file instanceof File || file instanceof Blob)) {
      console.error('❌ CRITICAL: Invalid file object for CSV formatting:', {
        hasFile: !!file,
        type: typeof file,
        constructor: file?.constructor?.name,
        isFile: file instanceof File,
        isBlob: file instanceof Blob
      });
      throw new Error(
        'Invalid file object for CSV formatting: Expected File or Blob instance, got ' +
        (file?.constructor?.name || typeof file)
      );
    }

    // Verify file has content (size > 0)
    if (!file.size || file.size === 0) {
      console.error('❌ File has no content (size=0)');
      throw new Error('Validation Error: Cannot validate empty file. Please select a file with content.');
    }

    console.log('✅ File object verified for API formatting:', {
      name: file.name,
      size: file.size,
      type: file.type,
      isFileInstance: file instanceof File
    });

    try {
      const client = this.vueContext?.$apiClient || apiClient;

      // Map ruleType to type parameter for CleverCSV API
      const ruleTypeMapping = {
        customCountries: 'customCountries',
        customSources: 'customSources',
        pkycFile: 'pkycFile',
        // Batch file types
        mnlBatch: 'mnlBatch',
        namesCompareBatch: 'namesCompareBatch',
        pi_aBatch: 'pi_aBatch',
        vesselAircraftBatch: 'vesselAircraftBatch',
        wireBatch: 'wireBatch',
        batchFiles: 'batchFiles',
      };
      const csvType = ruleTypeMapping[ruleType] || null;
      
      console.log('📋 CSV formatting request:', {
        ruleType,
        csvType,
        willIncludeTypeParam: !!csvType
      });
      
      // Create FormData for API call - consistent with API client signature
      const formData = new FormData();
      const fileName = file.name || 'upload.csv';
      
      console.log('📋 Creating FormData for formatCsvFile:', {
        fileName: fileName,
        fileType: file.type,
        fileSize: file.size,
        fileConstructor: file.constructor?.name
      });
      
      formData.append('file', file, fileName);
      
      console.log('🌐 Calling formatCsvFile API...', {
        endpoint: '/clevercsv',
        hasClient: !!client,
        hasVueContext: !!this.vueContext,
        csvType: csvType,
        fileName: fileName
      });
      const response = await client.formatCsvFile(file, formData, csvType);
      console.log('✅ formatCsvFile API call successful');

      // VALIDATE RESPONSE STRUCTURE
      if (!response) {
        console.error('❌ CleverCSV API returned no response');
        throw new Error('CleverCSV API returned no response');
      }

      if (!response.data) {
        console.error('❌ Malformed API response - missing data property:', response);
        throw new Error('CleverCSV API response missing data property');
      }

      console.log('🔍 formatCsvFile API response type:', typeof response.data);

      // Ensure response.data is a string (handle cases where it might be an object)
      let csvContent;
      if (typeof response.data === 'string') {
        csvContent = response.data;
      } else if (typeof response.data === 'object' && response.data !== null) {
        // If response.data is an object, try to extract the actual CSV content
        csvContent = response.data.content || response.data.data;

        if (!csvContent) {
          console.error('❌ API response object missing content:', response.data);
          throw new Error('CleverCSV API response missing formatted content');
        }

        console.warn('⚠️ formatCsvFile API returned object instead of string, extracted content');
      } else {
        csvContent = String(response.data);
      }

      if (!csvContent || csvContent.trim().length === 0) {
        throw new Error('CleverCSV API returned empty content');
      }

      // Create new File object with formatted content
      const formattedFile = new File([csvContent], file.name, {
        type: 'text/csv',
        lastModified: Date.now(),
      });

      console.log('📄 Created formatted file:', {
        name: formattedFile.name,
        type: formattedFile.type,
        size: formattedFile.size,
        constructor: formattedFile.constructor?.name,
        contentPreview: csvContent.substring(0, 100) + (csvContent.length > 100 ? '...' : '')
      });

      console.log('✅ CSV file formatted successfully using RFC4180 standard');
      return { file: formattedFile, content: csvContent };
      
    } catch (error) {
      console.error('❌ CSV formatting API failed:', {
        errorMessage: error.message,
        errorCode: error.code,
        errorResponse: error.response?.status,
        errorData: error.response?.data
      });

      // Check if this is an authentication/authorization issue
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Authentication Error: Login required. Please log in and try again.');
      }

      // Check if this is a network/connection issue
      const isNetworkError = error.message.includes('fetch') ||
                             error.message.includes('network') ||
                             error.code === 'NETWORK_ERROR' ||
                             error.code === 'ECONNABORTED' ||
                             !error.response;

      if (isNetworkError) {
        throw new Error('Network Error: Unable to connect to validation service. Please check your internet connection and try again.');
      }

      // API returned an error response
      if (error.response) {
        throw new Error(`Service Error: Validation service error. ${error.response.data?.message || error.message}. Please contact support if this persists.`);
      }

      // Unknown error
      throw new Error(`Validation Error: CSV validation failed. ${error.message}. Please ensure your file is a valid CSV and try again.`);
    }
  }

  /**
   * Normalize file object to ensure it has required properties
   * @param {File|Object} file - File or file-like object
   * @returns {File|Object} Normalized file object with guaranteed name property
   */
  _normalizeFileObject(file) {
    if (!file) return null;

    // CRITICAL: ALWAYS preserve File/Blob instances - NEVER spread them!
    // Spreading destroys File prototype methods (.text(), .arrayBuffer(), etc.)
    // and loses the actual file content!
    if (file instanceof File || file instanceof Blob) {
      // File/Blob objects must be returned as-is, even if name is missing
      // Missing name is rare but the File instance is still valid
      if (!file.name) {
        console.warn('File/Blob instance has no name property, but preserving File instance:', {
          type: file.type,
          size: file.size,
          constructor: file.constructor?.name
        });
      }
      return file; // Return original File/Blob unchanged
    }

    // If we reach here, it's NOT a File/Blob instance - this is unusual
    // Could be a test mock or incorrectly constructed object
    console.error('❌ CRITICAL: Received non-File/Blob object in validation:', {
      type: typeof file,
      constructor: file?.constructor?.name,
      hasName: !!file?.name,
      hasSize: file?.size !== undefined,
      hasType: !!file?.type,
      keys: file ? Object.keys(file) : []
    });

    // For non-File objects, if they have a proper name, assume it's a mock/test object
    if (file && file.name && typeof file.name === 'string' && file.name.trim() !== '') {
      console.warn('Non-File object with name property detected (test mock?), allowing through');
      return file;
    }

    // Otherwise, this is an invalid file object - FAIL
    throw new Error(
      'Invalid file object: Expected File or Blob instance, got ' +
      (file?.constructor?.name || typeof file) +
      '. This indicates a system error in file handling.'
    );
  }

  /**
   * Validate a file using rule-based validation
   * @param {File} file - The file to validate
   * @param {string} ruleType - The rule type to apply
   * @param {Object} additionalConfig - Additional configuration options
   * @returns {Promise<Object>} Validation result
   */
  async validateWithRules(file, ruleType, additionalConfig = {}) {
    console.log('🎯 ValidationRulesService.validateWithRules called', {
      fileName: file?.name,
      ruleType,
      hasVueContext: !!this.vueContext,
      additionalConfig,
    });

    // Normalize file object to ensure it has required properties
    let normalizedFile = this._normalizeFileObject(file);
    
    // Format CSV files through API BEFORE validation and get rules
    let formattedResult = null;
    if (this._isCsvFile(normalizedFile)) {
      console.log('📄 CSV file detected, formatting through API...');
      formattedResult = await this._formatCsvFileBeforeValidation(normalizedFile, ruleType);
      normalizedFile = formattedResult.file;
      console.log('✅ CSV file formatted successfully');
    }

    // Load validation rules from server AFTER formatting - CRITICAL for proper validation
    console.log('🌐 Loading validation rules from server...');
    let rules, globalSettings;

    try {
      rules = await this._loadRulesWithRetryAfterFormatting(ruleType);
      globalSettings = await this.getGlobalSettings();

      if (rules) {
        console.log('✅ Server validation rules loaded successfully:', { ruleType, parserType: rules.parserType });
      } else {
        console.warn('⚠️ No server rules found for rule type:', ruleType);
        console.warn('⚠️ This may indicate a server configuration issue or missing rule definition');
      }
    } catch (error) {
      console.error('❌ Failed to load validation rules from API:', {
        ruleType,
        errorMessage: error.message,
        errorCode: error.code,
        errorResponse: error.response?.status
      });

      // NO FALLBACK TO CACHED RULES - API validation is mandatory
      // This ensures files are always validated against the latest server-side rules

      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Authentication Error: Login required. Please log in and try again.');
      }

      const isNetworkError = !error.response ||
                             error.code === 'NETWORK_ERROR' ||
                             error.code === 'ECONNABORTED';

      if (isNetworkError) {
        throw new Error('Network Error: Unable to connect to validation service. Please check your internet connection and try again.');
      }

      // API error - must fail validation
      throw new Error(`Service Error: Unable to load validation rules for ${ruleType}. ${error.message}. Please try again or contact support.`);
    }
    
    console.log('📋 Retrieved rules and settings', {
      hasRules: !!rules,
      parserType: rules?.parserType,
      globalSettings: !!globalSettings,
    });

    if (!rules) {
      // No validation rules found - this is concerning especially after CSV formatting
      const isCSVFile = this._isCsvFile(normalizedFile);
      
      if (isCSVFile && formattedResult) {
        // CSV was formatted via API but no server rules found - this is likely a configuration issue
        console.error('❌ CRITICAL: CSV file was formatted via cleverCSV API but no server validation rules found!');
        console.error('❌ This suggests the server successfully formatted the CSV but failed to provide validation rules.');
        
        const errorMessage = `CSV file was formatted using cleverCSV API but no validation rules were received from server for rule type: ${ruleType}. This indicates a server configuration issue.`;
        return {
          isValid: false,
          errors: [{
            code: 'MISSING_SERVER_RULES_AFTER_CSV_FORMAT',
            message: errorMessage,
            timestamp: new Date().toISOString(),
          }],
          warnings: [],
          processedFile: normalizedFile,
          metadata: {
            ruleType,
            fileName: normalizedFile.name,
            fileSize: normalizedFile.size,
            validatedAt: new Date().toISOString(),
            csvFormattedButNoRules: true,
          },
        };
      } else {
        // Non-CSV file or CSV without formatting - allow basic validation
        console.warn(`⚠️ No server validation rules found for type: ${ruleType}. Using basic file validation.`);
        return {
          isValid: true,
          errors: [],
          warnings: [{
            code: 'NO_RULES_FOUND',
            message: `No validation rules found for type: ${ruleType}. Basic file validation applied.`,
            timestamp: new Date().toISOString(),
          }],
          processedFile: normalizedFile,
          metadata: {
            ruleType,
            fileName: normalizedFile.name,
            fileSize: normalizedFile.size,
            validatedAt: new Date().toISOString(),
            noRulesFound: true,
            basicValidationOnly: true,
          },
        };
      }
    }

    const result = {
      isValid: true,
      errors: [],
      warnings: [],
      metadata: {
        ruleType,
        fileName: normalizedFile.name,
        fileSize: normalizedFile.size,
        validatedAt: new Date().toISOString(),
      },
    };

    try {
      // File size validation using global settings
      if (typeof normalizedFile.size === 'number' && normalizedFile.size > globalSettings.maxFileSize) {
        this._addError(result, 'FILE_TOO_LARGE', 
          `File size (${this._formatSize(normalizedFile.size)}) exceeds maximum allowed size (${this._formatSize(globalSettings.maxFileSize)})`);
      } else if (normalizedFile.size === undefined || normalizedFile.size === null) {
        console.warn('File size is missing, skipping size validation');
      }

      // File type validation using global settings
      const fileName = normalizedFile.name;
      const fileExtension = fileName.includes('.') ? '.' + fileName.split('.').pop().toLowerCase() : '';
      if (fileExtension && !globalSettings.allowedFileTypes.includes(fileExtension)) {
        this._addError(result, 'INVALID_FILE_TYPE', 
          `File type ${fileExtension} is not allowed. Allowed types: ${globalSettings.allowedFileTypes.join(', ')}`);
      } else if (!fileExtension) {
        console.warn('File has no extension, skipping file type validation');
      }

      // Rule-specific validation based on parser type
      switch (rules.parserType) {
        case 'csv':
        case 'pipe': // Support both 'csv' and 'pipe' parser types for CSV files with delimiter auto-detection
          await this._validateCSVWithRules(normalizedFile, rules, result, additionalConfig, formattedResult?.content);
          break;
        case 'line': // Line-by-line validation for files where each line is a single value (e.g., excludedWords)
          await this._validateLineByLineWithRules(normalizedFile, rules, result, additionalConfig, formattedResult?.content);
          break;
        case 'xml':
          await this._validateXMLWithRules(normalizedFile, rules, result, additionalConfig);
          break;
        case 'ach':
          await this._validateACHWithRules(normalizedFile, rules, result, additionalConfig);
          break;
        case 'txt':
          await this._validateTXTWithRules(normalizedFile, rules, result, additionalConfig);
          break;
        default:
          throw new Error(`Unsupported parser type: ${rules.parserType}`);
      }

      console.log('🎉 ValidationRulesService validation completed', {
        isValid: result.isValid,
        errorCount: result.errors.length,
        warningCount: result.warnings.length,
      });
      
      // Include processed file in result (for CSV files this is the formatted version)
      result.processedFile = normalizedFile;
      return result;
    } catch (error) {
      console.error('❌ ValidationRulesService validation error:', error);
      this._addError(result, 'VALIDATION_ERROR', error.message);
      // Include original file even in error case
      result.processedFile = normalizedFile;
      return result;
    }
  }

  /**
   * Auto-detect CSV delimiter from content with backward compatibility
   * @param {string} content - CSV content to analyze
   * @param {string} fallbackDelimiter - Default delimiter from rules
   * @returns {string} Detected delimiter (',' or '|')
   */
  _detectCSVDelimiter(content, fallbackDelimiter = ',') {
    if (!content || typeof content !== 'string') {
      console.warn('⚠️ Invalid CSV content for delimiter detection, using fallback:', fallbackDelimiter);
      return fallbackDelimiter;
    }
    
    // Get first few lines for analysis (up to 10 lines or 1000 characters)
    const lines = content.split('\n').slice(0, 10);
    const sampleContent = lines.join('\n').substring(0, 1000);
    
    // Count occurrences of potential delimiters
    const commaCount = (sampleContent.match(/,/g) || []).length;
    const pipeCount = (sampleContent.match(/\|/g) || []).length;
    
    console.log('🔍 CSV delimiter detection:', {
      sampleLines: lines.length,
      commaCount,
      pipeCount,
      fallbackDelimiter
    });
    
    // Detection logic with backward compatibility priority
    // If both delimiters are present, prefer the one with more occurrences
    // If neither is significantly present, use fallback (usually comma)
    let detectedDelimiter = fallbackDelimiter;
    
    if (pipeCount > 0 && pipeCount >= commaCount) {
      // Pipe delimiter detected and is more frequent or equal to comma
      detectedDelimiter = '|';
      console.log('📍 Pipe delimiter detected for backward compatibility');
    } else if (commaCount > 0) {
      // Comma delimiter detected and is more frequent
      detectedDelimiter = ',';
      console.log('📍 Comma delimiter detected (standard)');
    }
    
    // Validation check: ensure the detected delimiter actually splits the content meaningfully
    const testLines = lines.filter(line => line.trim().length > 0);
    if (testLines.length > 0) {
      const firstLineFields = testLines[0].split(detectedDelimiter);
      if (firstLineFields.length <= 1 && detectedDelimiter !== fallbackDelimiter) {
        // Detected delimiter doesn't split the line, fallback to default
        console.warn('⚠️ Detected delimiter does not split content properly, using fallback:', fallbackDelimiter);
        detectedDelimiter = fallbackDelimiter;
      }
    }
    
    console.log('✅ Final delimiter decision:', detectedDelimiter);
    return detectedDelimiter;
  }

  /**
   * CSV validation using PapaParse and rules template
   * @param {File} file - The file being validated
   * @param {Object} rules - Validation rules from server
   * @param {Object} result - Result object to populate
   * @param {Object} additionalConfig - Additional configuration
   * @param {string} formattedContent - Pre-formatted CSV content (optional)
   */
  async _validateCSVWithRules(file, rules, result, additionalConfig, formattedContent = null) {
    console.log('📊 ValidationRulesService._validateCSVWithRules started', {
      fileName: file?.name,
      hasFormattedContent: !!formattedContent,
      rulesType: rules?.parserType,
    });
    
    let content;
    
    try {
      // Use pre-formatted content if available, otherwise format the file
      if (formattedContent) {
        console.log('📄 Using pre-formatted CSV content');
        content = formattedContent;
      } else {
        console.log('⚠️ No pre-formatted content, reading file directly (this should not happen for CSV files)');
        content = await file.text();
      }
      
    } catch (error) {
      console.error('❌ CSV content access failed:', error);
      throw new Error(`Failed to access CSV content: ${error.message}`);
    }
    
    try {
      // Auto-detect CSV delimiter with backward compatibility support
      const fallbackDelimiter = rules.validation?.delimiter || ',';
      const detectedDelimiter = this._detectCSVDelimiter(content, fallbackDelimiter);
      
      console.log('📋 CSV parsing configuration:', {
        originalDelimiter: fallbackDelimiter,
        detectedDelimiter,
        hasHeader: rules.validation?.hasHeader || false,
      });

      // Parse CSV using PapaParse with Promise wrapper for proper async handling
      const parseResults = await new Promise((resolve, reject) => {
        const parseConfig = {
          header: rules.validation?.hasHeader || false,
          skipEmptyLines: rules.validation?.skipEmptyLines || true,
          delimiter: detectedDelimiter, // Use auto-detected delimiter
          quoteChar: rules.validation?.quoteChar || '"',
          escapeChar: rules.validation?.escapeChar || '"',
          transformHeader: rules.validation?.trimHeaders ? (header) => header.trim() : undefined,
          transform: rules.validation?.trimWhitespace ? (value) => typeof value === 'string' ? value.trim() : value : undefined,
          complete: (results) => {
            resolve(results);
          },
          error: (error) => {
            reject(new Error(`Failed to parse CSV: ${error.message}`));
          },
        };
        
        Papa.parse(content, parseConfig);
      });
      
      // Add delimiter information to result metadata
      result.metadata.delimiter = detectedDelimiter;
      result.metadata.originalDelimiter = fallbackDelimiter;
      result.metadata.delimiterAutoDetected = detectedDelimiter !== fallbackDelimiter;
      
      // Now validate the parsed CSV data synchronously
      this._validateParsedCSV(parseResults, rules, result, file.name);
      
    } catch (error) {
      this._addError(result, 'CSV_VALIDATION_ERROR', error.message);
    }
  }

  /**
   * Validate parsed CSV data using rule templates
   */
  _validateParsedCSV(parseResults, rules, result, fileName) {
    const validation = rules.validation;
    const data = parseResults.data;
    const errors = parseResults.errors;

    // Handle parse errors
    if (errors && errors.length > 0) {
      errors.forEach(error => {
        this._addError(result, 'CSV_PARSE_ERROR',
          `Row ${error.row + 1}: ${error.message}`);
      });
    }

    // Check if we have header-based (object) or index-based (array) rows
    const hasHeaders = data && data.length > 0 && !Array.isArray(data[0]);

    // Validate required columns first (before checking for empty data)
    // Use parseResults.meta.fields if hasHeader=true in validation config
    if (validation.hasHeader && validation.requiredColumns) {
      // Get available columns from PapaParse metadata
      const availableColumns = parseResults.meta?.fields || [];

      if (availableColumns.length > 0) {
        // Check required columns exist
        validation.requiredColumns.forEach(requiredCol => {
          if (!availableColumns.includes(requiredCol)) {
            this._addError(result, 'MISSING_REQUIRED_COLUMN',
              `Required column "${requiredCol}" is missing from CSV`);
          }
        });
      }
    } else if (hasHeaders && validation.requiredColumns) {
      // Fallback: check from first data row for files without hasHeader config
      const availableColumns = data.length > 0 ? Object.keys(data[0]) : [];

      validation.requiredColumns.forEach(requiredCol => {
        if (!availableColumns.includes(requiredCol)) {
          this._addError(result, 'MISSING_REQUIRED_COLUMN',
            `Required column "${requiredCol}" is missing from CSV`);
        }
      });
    }

    // Now check for empty data (after column validation)
    if (!data || data.length === 0) {
      this._addError(result, 'NO_DATA', 'File contains no data rows');
      return;
    }

    // Track unique values per column (for uniqueness validation)
    const columnUniqueValues = {};

    // Apply validation rules based on template
    const validationRules = validation.rules || [];
    const processedValues = new Set();

    data.forEach((row, index) => {
      const rowNumber = index + 1;

      // Handle different row structures (array vs object)
      if (hasHeaders) {
        // Object-based rows with column names
        const columns = Object.keys(row);

        if (columns.length === 0) {
          if (!validation.allowEmptyRows) {
            this._addError(result, 'EMPTY_ROW', `Row ${rowNumber}: Empty row found`);
          }
          return;
        }

        // Apply column-specific validation
        if (validation.columnValidation) {
          columns.forEach(columnName => {
            const value = row[columnName];
            const columnRules = validation.columnValidation[columnName];

            if (columnRules) {
              // Apply each rule for this column
              columnRules.forEach(rule => {
                this._applyColumnValidationRule(value, columnName, rule, rowNumber, result, columnUniqueValues);
              });
            }
          });
        }

        // Apply generic validation rules to all values
        if (validationRules.length > 0) {
          Object.values(row).forEach((value, colIndex) => {
            if (value !== null && value !== undefined && value !== '') {
              this._applyValidationRules(value, validationRules, processedValues, result, rowNumber, colIndex + 1);
            }
          });
        }
      } else {
        // Array-based rows (no headers)
        const values = row;

        if (values.length === 0) {
          if (!validation.allowEmptyRows) {
            this._addError(result, 'EMPTY_ROW', `Row ${rowNumber}: Empty row found`);
          }
          return;
        }

        // Apply generic validation rules to each value
        values.forEach((value, colIndex) => {
          if (value !== null && value !== undefined && value !== '') {
            this._applyValidationRules(value, validationRules, processedValues, result, rowNumber, colIndex + 1);
          }
        });
      }
    });

    // Set metadata
    result.metadata.recordCount = data.length;
    result.metadata.uniqueValues = processedValues.size;
    result.metadata.columns = hasHeaders ? Object.keys(data[0]).length : data[0].length;
  }

  /**
   * Apply column-specific validation rule
   *
   * @param {*} value - The value to validate
   * @param {string} columnName - Name of the column
   * @param {Object} rule - Validation rule to apply
   * @param {number} rowNumber - Row number for error messages
   * @param {Object} result - Result object to populate with errors
   * @param {Object} columnUniqueValues - Track unique values per column
   */
  _applyColumnValidationRule(value, columnName, rule, rowNumber, result, columnUniqueValues) {
    const strValue = String(value || '');

    switch (rule.type) {
      case 'required':
        if (!value || strValue.trim() === '') {
          this._addError(result, 'COLUMN_REQUIRED',
            `Row ${rowNumber}: ${rule.description || `Column "${columnName}" is required`}`);
        }
        break;

      case 'minLength':
        if (value && strValue.length < rule.value) {
          this._addError(result, 'COLUMN_MIN_LENGTH',
            `Row ${rowNumber}: ${rule.description || `Column "${columnName}" must be at least ${rule.value} characters`}`);
        }
        break;

      case 'maxLength':
        if (value && strValue.length > rule.value) {
          this._addError(result, 'COLUMN_MAX_LENGTH',
            `Row ${rowNumber}: ${rule.description || `Column "${columnName}" must not exceed ${rule.value} characters`}`);
        }
        break;

      case 'uniqueness':
        if (value && strValue.trim() !== '') {
          // Initialize tracking for this column if needed
          if (!columnUniqueValues[columnName]) {
            columnUniqueValues[columnName] = new Map();
          }

          const checkValue = rule.caseSensitive ? strValue : strValue.toLowerCase();

          if (columnUniqueValues[columnName].has(checkValue)) {
            const firstRow = columnUniqueValues[columnName].get(checkValue);
            this._addError(result, 'COLUMN_DUPLICATE',
              `Row ${rowNumber}: ${rule.description || `Duplicate value "${value}" in column "${columnName}" (first seen in row ${firstRow})`}`);
          } else {
            columnUniqueValues[columnName].set(checkValue, rowNumber);
          }
        }
        break;

      case 'pattern':
        if (value && strValue.trim() !== '') {
          const regex = new RegExp(rule.value);
          if (!regex.test(strValue)) {
            this._addError(result, 'COLUMN_PATTERN_MISMATCH',
              `Row ${rowNumber}: ${rule.description || `Column "${columnName}" does not match required pattern`}`);
          }
        }
        break;

      case 'enum':
        if (value && strValue.trim() !== '') {
          const allowedValues = rule.values || [];
          const checkValue = rule.caseSensitive ? strValue : strValue.toLowerCase();
          const allowedSet = rule.caseSensitive
            ? new Set(allowedValues)
            : new Set(allowedValues.map(v => v.toLowerCase()));

          if (!allowedSet.has(checkValue)) {
            this._addError(result, 'COLUMN_INVALID_VALUE',
              `Row ${rowNumber}: ${rule.description || `Column "${columnName}" must be one of: ${allowedValues.join(', ')}`}`);
          }
        }
        break;

      default:
        console.warn(`Unknown column validation rule type: ${rule.type}`);
    }
  }

  /**
   * Line-by-line validation for files where each line represents one complete value
   * Used for excludedWords where commas are PART of the value, not delimiters
   * Each line in the file = ONE excluded word/phrase
   *
   * @param {File} file - File to validate
   * @param {Object} rules - Validation rules
   * @param {Object} result - Result object to populate
   * @param {Object} additionalConfig - Additional configuration
   * @param {string|null} formattedContent - Pre-formatted content if already loaded
   */
  async _validateLineByLineWithRules(file, rules, result, additionalConfig, formattedContent = null) {
    console.log('📄 Line-by-line validation started');

    let content;
    if (formattedContent) {
      content = formattedContent;
      console.log('✅ Using pre-formatted content for line-by-line validation');
    } else {
      // Safely read file content - handle both File objects and plain objects
      content = typeof file.text === 'function' ? await file.text() : String(file);
      console.log('✅ Loaded file content for line-by-line validation');
    }

    const validation = rules.validation;

    // Split by newlines - each line is ONE value
    // Support both Windows (CRLF) and Unix (LF) line endings
    const lines = content.split(/\r?\n/);
    const processedValues = new Set();
    const seenValues = new Set(); // Track seen values for uniqueness check (case-insensitive)

    console.log(`📋 Processing ${lines.length} lines as individual values`);

    lines.forEach((line, index) => {
      const rowNumber = index + 1;

      // Apply trimWhitespace if configured
      const processedLine = validation.trimWhitespace ? line.trim() : line;

      // Skip empty lines if configured
      if (validation.skipEmptyLines && processedLine === '') {
        console.log(`⏭️  Skipping empty line ${rowNumber}`);
        return;
      }

      // Treat entire line as single value - DO NOT split on commas or any delimiter!
      if (processedLine) {
        // Apply validation rules to the entire line
        this._applyValidationRules(
          processedLine,
          validation.rules || [],
          seenValues,
          result,
          `Line ${rowNumber}`,
          null // No column name for line-by-line validation
        );

        processedValues.add(processedLine);
      }
    });

    // Set metadata
    result.metadata.recordCount = lines.filter(l => {
      const processed = validation.trimWhitespace ? l.trim() : l;
      return !(validation.skipEmptyLines && processed === '');
    }).length;
    result.metadata.uniqueValues = processedValues.size;
    result.metadata.totalLines = lines.length;
    result.metadata.parserType = 'line';

    console.log(`✅ Line-by-line validation completed:`, {
      totalLines: lines.length,
      validRecords: result.metadata.recordCount,
      uniqueValues: result.metadata.uniqueValues,
      errors: result.errors.length
    });
  }

  /**
   * XML validation using fast-xml-parser and rules template
   */
  async _validateXMLWithRules(file, rules, result, additionalConfig) {
    const content = await file.text();
    const validation = rules.validation;

    try {
      // First validate XML syntax using fast-xml-parser
      const isValidXML = XMLValidator.validate(content, {
        allowBooleanAttributes: validation.allowBooleanAttributes || false,
        unpairedTags: validation.unpairedTags || [],
        alwaysCreateTextNode: false,
      });
      
      if (isValidXML !== true) {
        this._addError(result, 'XML_SYNTAX_ERROR', 
          `XML syntax validation failed: ${isValidXML.err.msg} at line ${isValidXML.err.line}`);
        return;
      }
      
      // Parse XML for structure validation
      const parser = new XMLParser({
        ignoreAttributes: validation.ignoreAttributes || false,
        parseAttributeValue: validation.parseAttributeValue || false,
        trimValues: validation.trimValues || true,
        ignoreNameSpace: validation.ignoreNameSpace || false,
        parseTagValue: validation.parseTagValue || true,
      });
      
      const parsedXML = parser.parse(content);
      
      // Apply template-based validation rules
      this._validateParsedXML(parsedXML, rules, result, content);
      
      // Set metadata
      result.metadata.rootElement = Object.keys(parsedXML)[0];
      result.metadata.structure = this._getXMLStructureSummary(parsedXML);
      
    } catch (error) {
      this._addError(result, 'XML_PARSE_ERROR', validation.errorMessage || error.message);
    }
  }

  /**
   * Validate parsed XML using rule templates
   */
  _validateParsedXML(parsedXML, rules, result, originalContent) {
    const validation = rules.validation;
    
    // Check required root element
    if (validation.requiredRootElement) {
      const rootElement = Object.keys(parsedXML)[0];
      if (rootElement !== validation.requiredRootElement) {
        this._addError(result, 'INVALID_ROOT_ELEMENT', 
          `Expected root element '${validation.requiredRootElement}', found '${rootElement}'`);
      }
    }
    
    // Check required elements
    if (validation.requiredElements && validation.requiredElements.length > 0) {
      this._checkRequiredElements(parsedXML, validation.requiredElements, result);
    }
    
    // Check element constraints
    if (validation.elementConstraints) {
      this._validateElementConstraints(parsedXML, validation.elementConstraints, result);
    }
    
    // Check maximum depth
    if (validation.maxDepth) {
      const maxDepth = this._calculateObjectDepth(parsedXML);
      if (maxDepth > validation.maxDepth) {
        this._addError(result, 'XML_TOO_DEEP', 
          `XML depth (${maxDepth}) exceeds maximum allowed depth (${validation.maxDepth})`);
      }
      result.metadata.maxDepth = maxDepth;
    }
    
    // Check encoding if specified
    if (validation.encoding) {
      const encodingMatch = originalContent.match(/encoding=["']([^"']+)["']/i);
      if (encodingMatch && encodingMatch[1].toLowerCase() !== validation.encoding.toLowerCase()) {
        this._addWarning(result, 'ENCODING_MISMATCH', 
          `Expected encoding ${validation.encoding}, found ${encodingMatch[1]}`);
      }
    }
  }
  
  /**
   * Calculate object depth recursively
   */
  _calculateObjectDepth(obj, currentDepth = 0) {
    if (!obj || typeof obj !== 'object') {
      return currentDepth;
    }
    
    let maxDepth = currentDepth;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const depth = this._calculateObjectDepth(obj[key], currentDepth + 1);
        maxDepth = Math.max(maxDepth, depth);
      }
    }
    
    return maxDepth;
  }
  
  /**
   * Get XML structure summary
   */
  _getXMLStructureSummary(parsedXML) {
    const summary = {
      elements: new Set(),
      totalElements: 0,
    };
    
    this._traverseXMLObject(parsedXML, summary);
    
    return {
      uniqueElements: summary.elements.size,
      totalElements: summary.totalElements,
      elementNames: Array.from(summary.elements),
    };
  }
  
  /**
   * Traverse XML object to collect structure information
   */
  _traverseXMLObject(obj, summary) {
    if (!obj || typeof obj !== 'object') {
      return;
    }
    
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        summary.elements.add(key);
        summary.totalElements++;
        
        if (typeof obj[key] === 'object') {
          this._traverseXMLObject(obj[key], summary);
        }
      }
    }
  }
  
  /**
   * Check required elements in parsed XML
   */
  _checkRequiredElements(parsedXML, requiredElements, result) {
    requiredElements.forEach(elementPath => {
      const exists = this._checkElementPath(parsedXML, elementPath);
      if (!exists) {
        this._addError(result, 'MISSING_REQUIRED_ELEMENT', 
          `Required element '${elementPath}' not found`);
      }
    });
  }
  
  /**
   * Check if element path exists in parsed XML
   */
  _checkElementPath(obj, path) {
    const parts = path.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (!current || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, part)) {
        return false;
      }
      current = current[part];
    }
    
    return true;
  }
  
  /**
   * Validate element constraints
   */
  _validateElementConstraints(parsedXML, constraints, result) {
    for (const [elementPath, constraint] of Object.entries(constraints)) {
      const value = this._getElementValue(parsedXML, elementPath);
      
      if (value !== null) {
        this._applyValidationRules(value, constraint.rules || [], new Set(), result, elementPath, 0);
      } else if (constraint.required) {
        this._addError(result, 'MISSING_REQUIRED_ELEMENT', 
          `Required element '${elementPath}' not found`);
      }
    }
  }
  
  /**
   * Get element value from parsed XML by path
   */
  _getElementValue(obj, path) {
    const parts = path.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (!current || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, part)) {
        return null;
      }
      current = current[part];
    }
    
    return current;
  }

  /**
   * ACH validation using pure ach.wasm validation (no API rules overlay)
   */
  async _validateACHWithRules(file, rules, result, additionalConfig) {
    console.log('🔧 Starting pure WASM ACH validation (no API rules overlay)');
    
    try {
      // Wait for WASM to be ready
      if (typeof window !== 'undefined' && window.wasmReady) {
        await window.wasmReady;
      } else {
        this._addError(result, 'WASM_NOT_AVAILABLE', 'ACH WASM module not available');
        return;
      }
      
      // Check if parseACH function is available
      if (typeof window.parseACH !== 'function') {
        this._addError(result, 'ACH_PARSER_NOT_LOADED', 'ACH parser not loaded');
        return;
      }
      
      // Read file content
      const content = await file.text();
      
      if (!content || content.trim().length === 0) {
        this._addError(result, 'EMPTY_FILE', 'ACH file cannot be empty');
        return;
      }
      
      // Parse ACH using WASM - this is the ONLY validation that matters
      let parsedResult;
      try {
        const wasmResult = window.parseACH(content);
        
        // Check if WASM returned valid JSON
        if (typeof wasmResult !== 'string' || wasmResult[0] !== '{') {
          this._addError(result, 'INVALID_ACH_FORMAT', 'Invalid ACH file format - WASM parser failed');
          return;
        }
        
        parsedResult = JSON.parse(wasmResult);
        
        // Pure WASM validation: if WASM can parse it and doesn't mark it as invalid, it's valid
        if (!parsedResult || typeof parsedResult !== 'object') {
          this._addError(result, 'INVALID_ACH_STRUCTURE', 'WASM parser returned invalid structure');
          return;
        }
        
        // If WASM result has a 'valid' property, respect it
        if (Object.prototype.hasOwnProperty.call(parsedResult, 'valid') && !parsedResult.valid) {
          this._addError(result, 'ACH_WASM_VALIDATION_FAILED', 'ACH file rejected by WASM validator');
          return;
        }
        
        console.log('✅ ACH file passed pure WASM validation');
        
      } catch (parseError) {
        this._addError(result, 'ACH_PARSE_ERROR', `ACH parsing failed: ${parseError.message}`);
        return;
      }
      
      // Set ACH-specific metadata (extracted from WASM results only)
      result.metadata.achData = this._extractACHMetadata(parsedResult);
      result.metadata.recordCount = this._countACHRecords(parsedResult);
      
    } catch (error) {
      this._addError(result, 'ACH_VALIDATION_ERROR', `ACH validation failed: ${error.message}`);
    }
  }
  
  /**
   * Basic ACH format validation
   */
  _isValidACHFormat(content, validation, result) {
    const lines = content.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      this._addError(result, 'NO_ACH_RECORDS', 'No ACH records found');
      return false;
    }
    
    // Check minimum record length (ACH records are typically 94 characters)
    if (validation.minRecordLength) {
      const invalidLines = lines.filter(line => line.length < validation.minRecordLength);
      if (invalidLines.length > 0) {
        this._addError(result, 'INVALID_RECORD_LENGTH', 
          `Found ${invalidLines.length} records shorter than ${validation.minRecordLength} characters`);
        return false;
      }
    }
    
    // Check record length consistency (all ACH records should be same length)
    if (validation.enforceRecordLength) {
      const expectedLength = validation.recordLength || 94;
      const invalidLines = lines.filter(line => line.length !== expectedLength);
      if (invalidLines.length > 0) {
        this._addError(result, 'INCONSISTENT_RECORD_LENGTH', 
          `Found ${invalidLines.length} records not matching expected length of ${expectedLength} characters`);
        return false;
      }
    }
    
    // Check for file header record (record type '1')
    if (validation.requireFileHeader) {
      const hasFileHeader = lines.some(line => line.length > 0 && line[0] === '1');
      if (!hasFileHeader) {
        this._addError(result, 'MISSING_FILE_HEADER', 'ACH file header record (type 1) not found');
        return false;
      }
    }
    
    // Check for file control record (record type '9')
    if (validation.requireFileControl) {
      const hasFileControl = lines.some(line => line.length > 0 && line[0] === '9');
      if (!hasFileControl) {
        this._addError(result, 'MISSING_FILE_CONTROL', 'ACH file control record (type 9) not found');
        return false;
      }
    }
    
    return true;
  }
  
  /**
   * Validate parsed ACH data using template rules
   */
  _validateParsedACH(parsedData, validationRules, result, originalContent) {
    if (!parsedData || typeof parsedData !== 'object') {
      this._addError(result, 'INVALID_PARSED_DATA', 'Parsed ACH data is invalid');
      return;
    }
    
    // Apply validation rules to different ACH components
    if (parsedData.fileHeader) {
      this._validateACHComponent(parsedData.fileHeader, validationRules, result, 'File Header');
    }
    
    if (parsedData.batches && Array.isArray(parsedData.batches)) {
      parsedData.batches.forEach((batch, index) => {
        this._validateACHComponent(batch, validationRules, result, `Batch ${index + 1}`);
        
        if (batch.entries && Array.isArray(batch.entries)) {
          batch.entries.forEach((entry, entryIndex) => {
            this._validateACHComponent(entry, validationRules, result, 
              `Batch ${index + 1}, Entry ${entryIndex + 1}`);
          });
        }
      });
    }
    
    if (parsedData.fileControl) {
      this._validateACHComponent(parsedData.fileControl, validationRules, result, 'File Control');
    }
  }
  
  /**
   * Validate individual ACH component
   */
  _validateACHComponent(component, validationRules, result, componentName) {
    if (!component || typeof component !== 'object') {
      return;
    }
    
    Object.entries(component).forEach(([field, value]) => {
      if (value !== null && value !== undefined) {
        this._applyValidationRules(value, validationRules, new Set(), result, componentName, field);
      }
    });
  }
  
  /**
   * Extract ACH metadata from parsed data
   */
  _extractACHMetadata(parsedData) {
    const metadata = {
      hasFileHeader: !!parsedData.fileHeader,
      hasFileControl: !!parsedData.fileControl,
      batchCount: 0,
      entryCount: 0,
      totalAmount: 0,
    };
    
    if (parsedData.fileHeader) {
      metadata.fileHeader = {
        immediateDestination: parsedData.fileHeader.immediateDestination,
        immediateOrigin: parsedData.fileHeader.immediateOrigin,
        fileCreationDate: parsedData.fileHeader.fileCreationDate,
        fileCreationTime: parsedData.fileHeader.fileCreationTime,
      };
    }
    
    if (parsedData.batches && Array.isArray(parsedData.batches)) {
      metadata.batchCount = parsedData.batches.length;
      
      parsedData.batches.forEach(batch => {
        if (batch.entries && Array.isArray(batch.entries)) {
          metadata.entryCount += batch.entries.length;
          
          batch.entries.forEach(entry => {
            if (entry.amount) {
              metadata.totalAmount += parseFloat(entry.amount) || 0;
            }
          });
        }
      });
    }
    
    return metadata;
  }
  
  /**
   * Count total ACH records
   */
  _countACHRecords(parsedData) {
    let recordCount = 0;
    
    if (parsedData.fileHeader) recordCount++;
    if (parsedData.fileControl) recordCount++;
    
    if (parsedData.batches && Array.isArray(parsedData.batches)) {
      parsedData.batches.forEach(batch => {
        recordCount++; // batch header
        recordCount++; // batch control
        
        if (batch.entries && Array.isArray(batch.entries)) {
          recordCount += batch.entries.length;
        }
      });
    }
    
    return recordCount;
  }

  /**
   * TXT validation for SWIFT MT799 and other text-based formats
   * @param {File} file - The file being validated
   * @param {Object} rules - Validation rules from server
   * @param {Object} result - Result object to populate
   * @param {Object} additionalConfig - Additional configuration
   */
  async _validateTXTWithRules(file, rules, result, additionalConfig) {
    console.log('📄 ValidationRulesService._validateTXTWithRules started', {
      fileName: file?.name,
      rulesType: rules?.parserType,
    });
    
    try {
      const content = await file.text();
      const validation = rules.validation;
      
      // Basic content validation
      if (!content || content.trim().length === 0) {
        this._addError(result, 'EMPTY_FILE', 'Text file cannot be empty');
        return;
      }
      
      // Apply encoding validation if specified
      if (validation.encoding && validation.encoding !== 'UTF-8') {
        console.warn(`TXT file validation: Expected encoding ${validation.encoding}, but browser always reads as UTF-8`);
      }
      
      // Apply line count validation if specified
      if (validation.maxLines) {
        const lines = content.split('\n');
        if (lines.length > validation.maxLines) {
          this._addError(result, 'TOO_MANY_LINES', 
            `File has ${lines.length} lines, maximum allowed is ${validation.maxLines}`);
        }
        result.metadata.lineCount = lines.length;
      }
      
      // Apply validation rules to the entire content
      const validationRules = validation.rules || [];
      
      // Apply each validation rule to the content
      validationRules.forEach((rule, index) => {
        try {
          this._applyTXTValidationRule(content, rule, result, file.name, index);
        } catch (ruleError) {
          console.error(`TXT validation rule ${index} failed:`, ruleError);
          this._addError(result, 'RULE_APPLICATION_ERROR', 
            `Validation rule ${index + 1} failed: ${ruleError.message}`);
        }
      });
      
      // Apply format-specific validation if specified (e.g., SWIFT MT799)
      if (validation.formatValidation?.swiftMT) {
        this._validateSWIFTFormat(content, validation.formatValidation.swiftMT, result);
      }
      
      // Set TXT-specific metadata
      result.metadata.contentLength = content.length;
      result.metadata.encoding = 'UTF-8';
      
      console.log('✅ TXT validation completed', {
        isValid: result.isValid,
        errorCount: result.errors.length,
      });
      
    } catch (error) {
      this._addError(result, 'TXT_VALIDATION_ERROR', `TXT validation failed: ${error.message}`);
    }
  }
  
  /**
   * Apply a single validation rule to TXT content
   * @param {string} content - File content
   * @param {Object} rule - Validation rule
   * @param {Object} result - Result object to populate
   * @param {string} fileName - File name for error context
   * @param {number} ruleIndex - Rule index for error context
   */
  _applyTXTValidationRule(content, rule, result, fileName, ruleIndex) {
    switch (rule.type) {
      case 'required':
        if (!content || content.trim() === '') {
          this._addError(result, 'REQUIRED_CONTENT_EMPTY', 
            rule.message || 'File content cannot be empty');
        }
        break;
        
      case 'pattern':
        try {
          const pattern = new RegExp(rule.value, rule.flags || '');
          const matches = pattern.test(content);
          
          // Handle optional patterns - only fail if rule is not marked as optional
          if (!matches && !rule.optional) {
            this._addError(result, 'INVALID_PATTERN', 
              rule.message || `Content does not match required pattern`);
          } else if (!matches && rule.optional) {
            console.log(`Optional pattern not matched: ${rule.message || rule.value}`);
          } else if (matches) {
            console.log(`Pattern matched: ${rule.message || rule.value}`);
          }
        } catch (patternError) {
          this._addError(result, 'INVALID_REGEX_PATTERN', 
            `Invalid regex pattern in rule ${ruleIndex + 1}: ${patternError.message}`);
        }
        break;
        
      case 'minLength':
        if (content.length < rule.value) {
          this._addError(result, 'CONTENT_TOO_SHORT', 
            rule.message || `Content is too short (minimum ${rule.value} characters)`);
        }
        break;
        
      case 'maxLength':
        if (content.length > rule.value) {
          this._addError(result, 'CONTENT_TOO_LONG', 
            rule.message || `Content is too long (maximum ${rule.value} characters)`);
        }
        break;
        
      default:
        console.warn(`Unknown TXT validation rule type: ${rule.type}`);
    }
  }
  
  /**
   * Validate SWIFT message format structure
   * @param {string} content - File content
   * @param {Object} swiftConfig - SWIFT format configuration
   * @param {Object} result - Result object to populate
   */
  _validateSWIFTFormat(content, swiftConfig, result) {
    try {
      // Validate required blocks are present
      if (swiftConfig.requireBlocks) {
        swiftConfig.requireBlocks.forEach(blockNumber => {
          const blockPattern = new RegExp(`\\{${blockNumber}:`);
          if (!blockPattern.test(content)) {
            this._addError(result, 'MISSING_SWIFT_BLOCK', 
              `Missing required SWIFT block {${blockNumber}:}`);
          }
        });
      }
      
      // Validate message type if specified
      if (swiftConfig.messageType) {
        result.metadata.swiftMessageType = swiftConfig.messageType;
      }
      
      // Validate Block 4 structure if specified
      if (swiftConfig.block4) {
        this._validateSWIFTBlock4(content, swiftConfig.block4, result);
      }
      
    } catch (error) {
      this._addError(result, 'SWIFT_FORMAT_ERROR', `SWIFT format validation failed: ${error.message}`);
    }
  }
  
  /**
   * Validate SWIFT Block 4 structure and tags
   * @param {string} content - File content
   * @param {Object} block4Config - Block 4 configuration
   * @param {Object} result - Result object to populate
   */
  _validateSWIFTBlock4(content, block4Config, result) {
    // Extract Block 4 content
    const block4Match = content.match(/\{4:(.*?)-\}/s);
    if (!block4Match) {
      this._addError(result, 'MISSING_BLOCK4', 'SWIFT Block 4 not found or malformed');
      return;
    }
    
    const block4Content = block4Match[1];
    
    // Check required tags
    if (block4Config.requiredTags) {
      block4Config.requiredTags.forEach(tagNumber => {
        const tagPattern = new RegExp(`^:${tagNumber}:`, 'm');
        if (!tagPattern.test(block4Content)) {
          this._addError(result, 'MISSING_SWIFT_TAG', 
            `Missing required SWIFT tag :${tagNumber}:`);
        }
      });
    }
    
    // Validate optional tags format (if present)
    if (block4Config.optionalTags) {
      block4Config.optionalTags.forEach(tagNumber => {
        const tagPattern = new RegExp(`^:${tagNumber}:`, 'm');
        if (tagPattern.test(block4Content)) {
          console.log(`Optional SWIFT tag :${tagNumber}: found and validated`);
        }
      });
    }
  }

  /**
   * Apply validation rules using template system
   */
  _applyValidationRules(value, validationRules, processedValues, result, rowInfo, colInfo) {
    const stringValue = String(value);
    
    validationRules.forEach(rule => {
      switch (rule.type) {
        case 'required':
          if (!stringValue || stringValue.trim() === '') {
            this._addError(result, 'REQUIRED_FIELD_EMPTY', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: Required field is empty`);
          }
          break;
          
        case 'minLength':
          if (stringValue.length < rule.value) {
            this._addError(result, 'VALUE_TOO_SHORT', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" is too short (minimum ${rule.value} characters)`);
          }
          break;
          
        case 'maxLength':
          if (stringValue.length > rule.value) {
            this._addError(result, 'VALUE_TOO_LONG', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" is too long (maximum ${rule.value} characters)`);
          }
          break;
          
        case 'pattern': {
          const pattern = new RegExp(rule.value, rule.flags || '');
          if (!pattern.test(stringValue)) {
            this._addError(result, 'INVALID_PATTERN', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" ${rule.message || 'does not match required pattern'}`);
          }
          break;
        }
          
        case 'uniqueness': {
          const uniqueKey = rule.caseSensitive ? stringValue : stringValue.toLowerCase();
          if (processedValues.has(uniqueKey)) {
            this._addError(result, 'DUPLICATE_VALUE', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: Duplicate value "${stringValue}"`);
          } else {
            processedValues.add(uniqueKey);
          }
          break;
        }
          
        case 'allowedValues': {
          const allowedValues = rule.caseSensitive 
            ? rule.values 
            : rule.values.map(v => v.toLowerCase());
          const checkValue = rule.caseSensitive ? stringValue : stringValue.toLowerCase();
          
          if (!allowedValues.includes(checkValue)) {
            this._addError(result, 'INVALID_VALUE', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" is not in allowed values: ${rule.values.join(', ')}`);
          }
          break;
        }
          
        case 'numeric': {
          if (isNaN(Number(stringValue))) {
            this._addError(result, 'INVALID_NUMERIC', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" is not a valid number`);
          }
          break;
        }
          
        case 'email': {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(stringValue)) {
            this._addError(result, 'INVALID_EMAIL', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" is not a valid email address`);
          }
          break;
        }
          
        case 'date': {
          const parsedDate = new Date(stringValue);
          if (isNaN(parsedDate.getTime())) {
            this._addError(result, 'INVALID_DATE', 
              `${rowInfo}${colInfo ? `, Column ${colInfo}` : ''}: "${stringValue}" is not a valid date`);
          }
          break;
        }
      }
    });
  }

  // Helper methods
  _addError(result, code, message) {
    result.errors.push({
      code,
      message,
      timestamp: new Date().toISOString(),
    });
    result.isValid = false;
  }

  _addWarning(result, code, message) {
    result.warnings.push({
      code,
      message,
      timestamp: new Date().toISOString(),
    });
  }

  _formatSize(bytes) {
    if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) {
      return 'unknown size';
    }
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Fallback default rules if API is unavailable
   */
  _getDefaultRules() {
    return {
      validationRules: {
        customCountries: {
          name: 'Custom Countries Validation',
          description: 'Template-based rules for custom countries validation with auto-delimiter detection',
          parserType: 'csv',
          validation: {
            hasHeader: false,
            skipEmptyLines: true,
            delimiter: ',', // Default delimiter - auto-detection supports both ',' and '|' for backward compatibility
            supportedDelimiters: [',', '|'], // Supported delimiters for auto-detection
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 2 },
              { type: 'maxLength', value: 100 },
              {
                type: 'pattern',
                value: '^[A-Za-z.\\-\';` ]+$',
                message: 'contains invalid characters. Only letters, dots, hyphens, apostrophes, semicolons, backticks and spaces are allowed',
              },
              { type: 'uniqueness', caseSensitive: false },
            ],
          },
        },
        excludedWords: {
          name: 'Excluded Words Validation',
          description: 'Free text filter validation - treats each line as one complete phrase',
          parserType: 'line',
          validation: {
            hasHeader: false,
            skipEmptyLines: true,
            trimWhitespace: true,
            allowEmptyRows: false,
            treatEachLineAsValue: true,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 255 },
              {
                type: 'pattern',
                value: '^[A-Za-z0-9\\s\\.\\-_,\';`]+$',
                message: 'contains invalid characters. Only letters, numbers, spaces, dots, hyphens, underscores, commas, apostrophes, semicolons, and backticks are allowed',
              },
              { type: 'uniqueness', caseSensitive: false },
            ],
          },
        },
        tf360: {
          name: 'TF360 Document Validation',
          description: 'Template-based rules for TF360 XML document validation',
          parserType: 'xml',
          validation: {
            allowBooleanAttributes: false,
            ignoreAttributes: false,
            trimValues: true,
            parseAttributeValue: false,
            maxDepth: 15,
            encoding: 'UTF-8',
            requiredElements: [],
            elementConstraints: {},
          },
        },
        achFile: {
          name: 'ACH File Validation',
          description: 'Template-based rules for ACH file validation using ach.wasm',
          parserType: 'ach',
          validation: {
            validateFormat: true,
            minRecordLength: 94,
            recordLength: 94,
            enforceRecordLength: true,
            requireFileHeader: true,
            requireFileControl: true,
            rules: [
              { type: 'required' },
              { 
                type: 'pattern', 
                value: '^[A-Za-z0-9\\s]+$',
                message: 'contains invalid characters for ACH format',
              },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 22 }, // Standard ACH field max length
            ],
          },
        },
        pkycFile: {
          name: 'PKYC File Validation',
          description: 'Validation rules for PKYC CSV files with compliance requirements and auto-delimiter detection',
          parserType: 'csv',
          validation: {
            hasHeader: true,
            skipEmptyLines: true,
            delimiter: ',', // Default delimiter - auto-detection supports both ',' and '|' for backward compatibility
            supportedDelimiters: [',', '|'], // Supported delimiters for auto-detection
            trimWhitespace: true,
            allowEmptyRows: false,
            requiredColumns: ['name', 'id', 'type'], // PKYC required fields
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 2 },
              { type: 'maxLength', value: 255 },
              {
                type: 'pattern',
                value: '^[A-Za-z0-9\\s\\.\\-_,\';`]+$',
                message: 'contains invalid characters. Only letters, numbers, spaces, dots, hyphens, underscores, commas, apostrophes, semicolons, and backticks are allowed',
              },
              { type: 'uniqueness', caseSensitive: false },
            ],
          },
        },
        customSources: {
          name: 'Custom Sources Validation',
          description: 'Validation rules for custom source CSV files with auto-delimiter detection',
          parserType: 'csv', 
          validation: {
            hasHeader: false,
            skipEmptyLines: true,
            delimiter: ',', // Default delimiter - auto-detection supports both ',' and '|' for backward compatibility
            supportedDelimiters: [',', '|'], // Supported delimiters for auto-detection
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 3 },
              { type: 'maxLength', value: 200 },
              {
                type: 'pattern',
                value: '^[A-Za-z0-9\\s\\.\\-_\';`]+$',
                message: 'contains invalid characters. Only letters, numbers, spaces, dots, hyphens, underscores, apostrophes, semicolons, and backticks are allowed',
              },
              { type: 'uniqueness', caseSensitive: false },
            ],
          },
        },
        // Add pipe-delimited CSV files support (mirrors customSources but with 'pipe' parser type)
        pipeDelimitedFiles: {
          name: 'Pipe-Delimited Files Validation',
          description: 'Validation rules for pipe-delimited CSV files with auto-delimiter detection',
          parserType: 'pipe',
          validation: {
            hasHeader: false,
            skipEmptyLines: true,
            delimiter: '|', // Default delimiter for pipe type, but auto-detection will override
            supportedDelimiters: [',', '|'], // Supported delimiters for auto-detection
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 200 },
              {
                type: 'pattern',
                value: '^[A-Za-z0-9\\s\\.\\-_|,\';`]+$',
                message: 'contains invalid characters. Only letters, numbers, spaces, dots, hyphens, underscores, pipes, commas, apostrophes, semicolons, and backticks are allowed',
              },
              { type: 'uniqueness', caseSensitive: false },
            ],
          },
        },
        // Batch validation rules - mirrors API validation_rules.json
        mnlBatch: {
          name: 'MNL Batch Validation',
          description: 'Manual Name List batch files for AML screening',
          parserType: 'pipe',
          validation: {
            hasHeader: true,
            skipEmptyLines: true,
            delimiter: '|',
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 500 },
            ],
          },
        },
        namesCompareBatch: {
          name: 'Names Compare Batch Validation',
          description: 'Name comparison batch files for fuzzy matching',
          parserType: 'pipe',
          validation: {
            hasHeader: true,
            skipEmptyLines: true,
            delimiter: '|',
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 2 },
              { type: 'maxLength', value: 500 },
            ],
          },
        },
        pi_aBatch: {
          name: '314a Batch Validation',
          description: '314a batch files for AML compliance',
          parserType: 'pipe',
          validation: {
            hasHeader: true,
            skipEmptyLines: true,
            delimiter: '|',
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 500 },
            ],
          },
        },
        vesselAircraftBatch: {
          name: 'Vessel-Aircraft Batch Validation',
          description: 'Vessel and aircraft batch files for screening',
          parserType: 'pipe',
          validation: {
            hasHeader: true,
            skipEmptyLines: true,
            delimiter: '|',
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 500 },
            ],
          },
        },
        wireBatch: {
          name: 'Instant Payment Batch Validation',
          description: 'Instant Payment transfer batch files for payment screening',
          parserType: 'pipe',
          validation: {
            hasHeader: true,
            skipEmptyLines: true,
            delimiter: '|',
            trimWhitespace: true,
            allowEmptyRows: false,
            rules: [
              { type: 'required' },
              { type: 'minLength', value: 1 },
              { type: 'maxLength', value: 500 },
            ],
          },
        },
      },
      globalSettings: this._getDefaultGlobalSettings(),
    };
  }

  _getDefaultGlobalSettings() {
    return {
      maxFileSize: 52428800, // 50MB
      allowedFileTypes: ['.csv', '.txt', '.xml', '.ach'],
      encoding: 'UTF-8',
      maxRows: 50000,
      errorHandling: {
        stopOnFirstError: false,
        maxErrors: 100,
        logErrors: true,
      },
    };
  }

  /**
   * Clear cached rules (useful for testing or when rules change)
   */
  clearCache() {
    this.rules = null;
    this.isLoaded = false;
    this.loadPromise = null;
  }

  /**
   * Directly set validation rules (useful for testing without API)
   * @param {Object} rules - Validation rules object with validationRules and globalSettings
   */
  setRules(rules) {
    if (!rules || !rules.validationRules || !rules.globalSettings) {
      throw new Error('Invalid rules object. Must contain validationRules and globalSettings');
    }
    this.rules = rules;
    this.isLoaded = true;
    console.log('✅ Validation rules set directly (testing mode)');
  }
}

// Create and export singleton instance
const validationRulesService = new ValidationRulesService();

export default validationRulesService;